import React, {Component} from "react"
import classNames from "classnames"
import {JsonDocument, Model} from "Model"
import {observer} from "mobx-react"
import {observable} from "mobx"
import Swal from "sweetalert2"
import styles from "./UploadInput.module.css"
import {ALLOWED_UPLOAD_DOCS_FORMAT} from "lib/functions/helper"

interface Props {
    title: string
    btnId: string
    uploads: any
    model: Model
    iconType?: "id" | "school" | "military" | "flip" | "ssn"
    description?: string
}

@observer
export class UploadInput extends Component<Props, {}> {
    @observable
    private errors: {[key: string]: string} = {}

    @observable
    private staticFile = {
        id: -1,
        name: "",
        url: ""
    }

    private json_data: JsonDocument = {}

    private CONSTANT_STATIC_PATH = "/uploads"

    private FILENAME_DASH = "_"
    private FILENAME_DOT = "."
    private START_INDEX_FILENAME = 0

    constructor(props) {
        super(props)
        this.json_data = props.model.document.json_data

        this.syncStaticFile()

        this.onModel = this.onModel.bind(this)
        props.model.dispatcher.addListener(this.onModel)
    }

    private syncStaticFile() {
        if (this.json_data.uploads && this.json_data.uploads.length > 0) {
            const signedUrl = this.json_data.uploadDocsSignedUrls || {}
            const findStaticFile = this.json_data.uploads.find(
                ({type}) => type === this.props.btnId
            )
            if (findStaticFile) {
                this.staticFile = {...findStaticFile}
                this.staticFile.url = `${findStaticFile.path}${
                    signedUrl[findStaticFile.type] || ""
                }`
                this.staticFile.name = this.cutFilename(findStaticFile.name)
            } else {
                this.staticFile = {
                    id: -1,
                    name: "",
                    url: ""
                }
            }
        } else {
            this.staticFile = {
                id: -1,
                name: "",
                url: ""
            }
        }
    }

    private onModel() {
        this.json_data = this.props.model.document.json_data
        this.syncStaticFile()
        this.forceUpdate()
    }

    public async componentDidMount() {
        this.props.model.dispatcher.dispatch()
    }

    public async componentWillUnmount() {
        this.props.model.dispatcher.removeListener(this.onModel)
    }

    private onHandleChangeButtonUpload = async (id: string, {target: {files}}) => {
        if (!files || files.length === 0) {
            return
        }
        const fileSize = files.item(0).size / 1024 / 1024 // in MB
        console.log("UploadInput:", {id, files})
        if (fileSize < 6) {
            const [aFile] = files
            const response = await this.props.model.uploadDocument(aFile, id)
            if (response.success) {
                if (this.json_data.isSubmitted) {
                    delete this.json_data.isSubmitted
                }
                const uploadDoc = response.data.upload
                if (!this.json_data.uploadDocsSignedUrls) {
                    this.json_data.uploadDocsSignedUrls = {
                        [id]: response.data.signedUrl
                    }
                } else {
                    this.json_data.uploadDocsSignedUrls[id] = response.data.signedUrl
                }
                this.replaceCurrentUploadFIle(id, uploadDoc)
                await this.props.model.persistJsonData(this.json_data)
            }
        } else {
            console.log("File size exceeds 6 MiB", fileSize)
            Swal.fire({
                icon: "warning",
                title: "Maximum file size reached",
                text:
                    "Your file size is above 6MB, please compress your image or file and then try again."
            })
        }
        this.forceUpdate()
    }

    private cutFilename = (fileName) => {
        if (fileName && fileName.lastIndexOf(this.FILENAME_DOT) > -1) {
            const lastIndexOfDash = fileName.lastIndexOf(this.FILENAME_DASH)
            const lastIndexOfDot = fileName.lastIndexOf(this.FILENAME_DOT)
            if (lastIndexOfDash > -1) {
                const newName = `${fileName.substring(
                    this.START_INDEX_FILENAME,
                    lastIndexOfDash
                )}${fileName.substring(lastIndexOfDot, fileName.length)}`
                return newName
            }
        }
        return fileName
    }

    private replaceCurrentUploadFIle = (id, file) => {
        const uploads = this.props.uploads
        if (uploads.length > 0) {
            const foundIndexUpload = uploads.findIndex((aFile) => aFile.type === id)
            const signedUrl = this.json_data.uploadDocsSignedUrls || {}
            if (foundIndexUpload > -1) {
                uploads[foundIndexUpload] = {...file}
                this.staticFile = {...file}
                this.staticFile.url = `${uploads[foundIndexUpload].path}${
                    signedUrl[uploads[foundIndexUpload].type] || ""
                }`
                this.staticFile.name = this.cutFilename(uploads[foundIndexUpload].name)
            } else {
                uploads.push(file)
                const foundFile = uploads.find((aFile) => aFile.type === id)
                this.staticFile = {...foundFile}
                this.staticFile.url = `${foundFile.path}${signedUrl[foundFile.type] || ""}`
                this.staticFile.name = this.cutFilename(foundFile.name)
            }
        } else {
            uploads.push(file)
            const [first] = uploads
            const signedUrl = this.json_data.uploadDocsSignedUrls || {}
            this.staticFile = {...file}
            this.staticFile.url = `${first.path}${signedUrl[first.type] || ""}`
            this.staticFile.name = this.cutFilename(file.name)
        }
        this.json_data.uploads = uploads
    }

    private showDocument = () => {
        const uploads = this.props.uploads
        if (uploads && uploads.length > 0) {
            const foundIndex = uploads.findIndex((aFile) => aFile.type === this.props.btnId)
            return foundIndex > -1
        }
        return false
    }

    private renderSlotIcon = (isUploaded: boolean) => {
        const {iconType} = this.props
        if (iconType === "school") {
            return (
                <svg
                    className={styles.slotIconSvg}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                    <path d="M6 12v5c3 3 9 3 12 0v-5" />
                </svg>
            )
        }
        if (iconType === "military") {
            return (
                <svg
                    className={styles.slotIconSvg}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
            )
        }
        if (iconType === "flip") {
            return (
                <svg
                    className={styles.slotIconSvg}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M7 16V4m0 0L3 8m4-4l4 4" />
                    <path d="M17 8v12m0 0l4-4m-4 4l-4-4" />
                </svg>
            )
        }
        if (iconType === "ssn") {
            return (
                <svg
                    className={styles.slotIconSvg}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
            )
        }
        // Default: ID card
        return (
            <svg
                className={styles.slotIconSvg}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <circle cx="8" cy="10" r="2" />
                <path d="M14 8h4" />
                <path d="M14 12h4" />
                <path d="M6 16h12" />
            </svg>
        )
    }

    public render() {
        const {title, btnId, description} = this.props
        const {onHandleChangeButtonUpload, showDocument, staticFile} = this
        const isUploaded = showDocument()

        const {
            csv,
            doc,
            docx,
            gif,
            jpegOrjpg,
            odp,
            ods,
            odt,
            png,
            pdf,
            ppt,
            pptx,
            rtf,
            txt,
            xls,
            xlsx
        } = ALLOWED_UPLOAD_DOCS_FORMAT
        const acceptFormats = `${csv}, ${doc}, ${docx}, ${gif}, ${jpegOrjpg}, ${odp}, ${ods}, ${odt}, ${png}, ${pdf}, ${ppt}, ${pptx}, ${rtf}, ${txt}, ${xls}, ${xlsx}`

        return (
            <div
                className={classNames(
                    styles.slotCard,
                    isUploaded ? styles.slotCardUploaded : styles.slotCardEmpty
                )}>
                <input
                    type="file"
                    accept={acceptFormats}
                    id={btnId}
                    onChange={(e) => {
                        onHandleChangeButtonUpload(btnId, e)
                    }}
                    hidden
                />

                <div className={styles.slotMain}>
                    <div
                        className={classNames(
                            styles.slotIconWrapper,
                            isUploaded ? styles.slotIconUploaded : styles.slotIconEmpty
                        )}>
                        {this.renderSlotIcon(isUploaded)}
                    </div>

                    <div className={styles.slotDetails}>
                        <div className={styles.slotTitleRow}>
                            <h3 className={styles.slotTitle}>{title}</h3>
                            {isUploaded ? (
                                <span className={styles.statusBadgeUploaded}>
                                    <svg
                                        className={styles.statusBadgeIcon}
                                        viewBox="0 0 12 12"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M2.5 6.2L5 8.7L9.5 3.3"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    Uploaded
                                </span>
                            ) : (
                                <span className={styles.statusBadgeEmpty}>Not Uploaded</span>
                            )}
                        </div>

                        {isUploaded && (
                            <div className={styles.fileMetaRow}>
                                <span className={styles.fileName}>{staticFile.name}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.slotActions}>
                    {isUploaded ? (
                        <>
                            <a
                                href={staticFile.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.btnPreview}
                                title="Preview uploaded file">
                                <svg
                                    className={styles.actionIcon}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                                <span>Preview</span>
                            </a>

                            <label
                                htmlFor={btnId}
                                className={styles.btnReplace}
                                title="Replace current file">
                                <svg
                                    className={styles.actionIcon}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                    <line x1="16" y1="5" x2="22" y2="5" />
                                    <line x1="19" y1="2" x2="19" y2="8" />
                                </svg>
                                <span>Replace</span>
                            </label>

                            <button
                                type="button"
                                className={styles.btnDelete}
                                title="Delete file"
                                onClick={async () => {
                                    const model = this.props.model
                                    const result = await Swal.fire({
                                        title: "Delete Document?",
                                        text: `Are you sure you want to delete ${staticFile.name}?`,
                                        icon: "warning",
                                        showCancelButton: true,
                                        confirmButtonColor: "#e11d48",
                                        cancelButtonColor: "#64748b",
                                        confirmButtonText: "Yes, delete it"
                                    })
                                    if (!result.isConfirmed) {
                                        return
                                    }
                                    const responseDocument = await model.deleteSupportedDocument(
                                        this.staticFile.id
                                    )
                                    if (responseDocument.success) {
                                        const filteredUploadedDocs = (
                                            this.json_data.uploads || []
                                        ).filter((uploadDoc) => uploadDoc.id !== this.staticFile.id)
                                        this.json_data.uploads = filteredUploadedDocs
                                        await this.props.model.persistJsonData(this.json_data)
                                    }
                                    this.syncStaticFile()
                                    this.forceUpdate()
                                }}>
                                <svg
                                    className={styles.actionIcon}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                                <span>Delete</span>
                            </button>
                        </>
                    ) : (
                        <label htmlFor={btnId} className={styles.btnSelectFile}>
                            <svg
                                className={styles.selectFileIcon}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            <span>Select File</span>
                        </label>
                    )}
                </div>
            </div>
        )
    }
}
